<template>
  <canvas :id="name"></canvas>
</template>

<script>
import Chart from 'chart.js';
import { debounce } from 'lodash';

export default {
  name: 'NmtSidebarProfileChart',
  props: {
    name: {
      type: String,
      default: 'chartNmt',
    },
    heights: {
      type: Array,
      required: true,
    },
    length: {
      type: String,
      required: true,
    },
    interval: {
      type: Number,
      required: true,
    },
    fresnelZonePoints: {
      type: Array,
    },
  },
  watch: {
    heights() {
      this.init();
    },
    fresnelZonePoints: {
      handler(nV, oV) {
        if (JSON.stringify(oV) === JSON.stringify(nV)) {
          return;
        }
        this.$nextTick(() => {
          if (!this.chart) {
            return;
          } else {
            const nVc = JSON.parse(JSON.stringify(nV));
            this.chart.data.datasets.splice(2, 1);
            this.chart.data.datasets.splice(1, 1);
            if (nVc?.length) {
              const dataset1 = {
                showLine: true,
                label: `- ${this.$i18n.t('sidebar.fresnelZone.fresnelAxisChartLabel')}`,
                data: [...nVc.splice(0, 1), ...nVc.splice(nVc.length - 1, 1)],
                fill: this.$_colors.success,
                borderColor: this.$_colors.font,
                borderWidth: 1,
              };
              const dataset2 = {
                label: `- ${this.$i18n.t('sidebar.fresnelZone.fresnelZoneChartLabel')}`,
                data: nVc,
                fill: this.$_colors.success,
                borderColor: this.$_colors.success,
                borderWidth: 1,
              };
              this.chart.data.datasets.push(dataset1);
              this.chart.data.datasets.push(dataset2);
            }
            this.chart.update();
          }
        });
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    getData() {
      const lastIndex = this.heights.length - 1;
      return this.heights.map((height, index) =>
        index === lastIndex ? { y: height, x: this.length } : { y: height, x: index * this.interval }
      );
    },
    drawChart(data) {
      Chart.plugins.register({
        afterRender: function(c) {
          const ctx = c.chart.ctx;
          ctx.save();
          ctx.globalCompositeOperation = 'destination-over';
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, c.chart.width, c.chart.height);
          ctx.restore();
        },
      });
      const canvas = document.getElementById(this.name);
      this.chart = new Chart(canvas, {
        data: {
          datasets: [
            {
              showLine: true,
              label: `- ${this.$_capitalize(this.$i18n.t('sidebar.nmtHeight'))} [m ${this.$i18n.t(
                'sidebar.nmtAboveSeaLevelAbbr'
              )}]`,
              data,
              fill: false,
              borderColor: this.$_colors.primary,
              borderWidth: 1,
            },
          ],
        },
        type: 'scatter',
        options: {
          maintainAspectRatio: true,
          responsive: true,
          title: {
            display: false,
          },
          scales: {
            yAxes: [
              {
                scaleLabel: {
                  display: true,
                  labelString: `[m ${this.$i18n.t('sidebar.nmtAboveSeaLevelAbbr')}]`,
                },
              },
            ],
            xAxes: [
              {
                scaleLabel: {
                  display: true,
                  labelString: '[m]',
                },
              },
            ],
          },
          events: ['mousemove'],
          onHover: debounce((e, chartElement) => {
            if (chartElement.length === 0) {
              this.$root.$emit('removeMarker');
              return;
            }
            const [element] = chartElement;
            if (element._datasetIndex !== 0) {
              return;
            }
            const fraction = element._index / (data.length - 1);
            this.$emit('drawMarker', fraction);
          }, 50),
        },
      });
    },
    downloadChart(name) {
      this.$_saveFile(this.chart.toBase64Image(), name ? name : this.$i18n.t('dialog.body.chart'));
    },
    init() {
      this.drawChart(this.getData());
    },
  },
  mounted() {
    this.init();
  },
};
</script>

<style lang="scss" scoped></style>
